import { useEffect, useState, useCallback } from "react";
import Joyride, { STATUS, type CallBackProps, type Step, type TooltipRenderProps } from "react-joyride";
import { useQuery } from "@tanstack/react-query";
import { AppWindow, Key, UsersRound, Users, type LucideIcon } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useMobile } from "@/hooks/use-mobile";
import { useSidebar } from "@/hooks/use-sidebar";
import { cn } from "@/lib/utils";

const TOUR_DONE_KEY_PREFIX = "saasgama_setup_tour_done_";
// Dispatched (e.g. from the Help menu) to manually restart the tour after it's been completed/skipped.
export const REPLAY_TOUR_EVENT = "saasgama:replay-setup-tour";
// Give the mobile menu sheet time to open/animate in before Joyride measures its target.
const MOBILE_MENU_OPEN_DELAY_MS = 400;

// Icon shown in each step's badge — mirrors the sidebar icon for the item being highlighted.
type TourStep = Step & { icon: LucideIcon };

const APPLICATIONS_STEP: TourStep = {
  target: '[data-tour="nav-applications"]',
  title: "Step 1 — Add your applications",
  content: "Start here. Add the SaaS tools your company uses — e.g. Slack, Notion, Figma.",
  placement: "right",
  disableBeacon: true,
  icon: AppWindow,
};

const LICENSES_STEP: TourStep = {
  target: '[data-tour="nav-licenses"]',
  title: "Step 2 — Add licenses",
  content: "Once you've added an application, come here to track its cost, seats and renewal date.",
  placement: "right",
  disableBeacon: true,
  icon: Key,
};

// Only visible to admins — regular team members don't have these sidebar items at all.
const PEOPLE_STEP: TourStep = {
  target: '[data-tour="nav-people"]',
  title: "Step 3 — Add your people",
  content: "Add employees so you can assign license seats to them.",
  placement: "right",
  disableBeacon: true,
  icon: UsersRound,
};

const TEAM_STEP: TourStep = {
  target: '[data-tour="nav-team"]',
  title: "Step 4 — Invite your team",
  content: "Give teammates login access to manage the dashboard with you.",
  placement: "right",
  disableBeacon: true,
  icon: Users,
};

function TourTooltip({
  index,
  size,
  step,
  isLastStep,
  primaryProps,
  skipProps,
  tooltipProps,
}: TooltipRenderProps) {
  const Icon = (step as TourStep).icon;
  const isMobile = useMobile();

  return (
    <div
      {...tooltipProps}
      // Radix's Sheet sets `pointer-events: none` on <body> while the mobile menu is
      // open (to trap focus/interaction inside it), which this tooltip would otherwise
      // inherit since it renders in its own react-floater portal outside the sheet.
      className="pointer-events-auto relative w-[280px] animate-in rounded-2xl bg-slate-900 p-4 shadow-2xl shadow-black/40 ring-1 ring-white/10 duration-200 fade-in-0 zoom-in-95"
    >
      {/* Hand-drawn curved pointer toward the highlighted nav item. Desktop keeps the
          configured placement:"right" (target to the left); the mobile sheet is too
          narrow for that, so react-floater flips the tooltip below the target instead. */}
      {isMobile ? (
        <svg
          aria-hidden="true"
          viewBox="0 0 44 40"
          fill="none"
          className="pointer-events-none absolute -top-9 left-6 h-10 w-11 text-cyan-400"
        >
          <path
            d="M36 36C28 24 18 12 8 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="4 4"
          />
          <path
            d="M4 12L8 6L15 10"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg
          aria-hidden="true"
          viewBox="0 0 44 24"
          fill="none"
          className="pointer-events-none absolute -left-9 top-1/2 h-6 w-11 -translate-y-1/2 text-cyan-400"
        >
          <path
            d="M40 8C26 4 16 20 6 12"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="4 4"
          />
          <path
            d="M12 7L6 12L13 15"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}

      <div className="flex items-start gap-2.5">
        {Icon && (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 shadow-lg shadow-black/30">
            <Icon className="h-4 w-4 text-white" />
          </div>
        )}
        <div className="min-w-0 flex-1 pt-0.5">
          {step.title && (
            <h3 className="text-sm font-semibold tracking-tight text-white">{step.title}</h3>
          )}
          {step.content && (
            <div className="mt-1 text-[13px] leading-relaxed text-slate-400">{step.content}</div>
          )}
        </div>
      </div>

      <div className="mt-3.5 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {Array.from({ length: size }).map((_, i) => (
            <span
              key={i}
              className={cn(
                "h-1.5 rounded-full transition-all duration-200",
                i === index ? "w-4 bg-cyan-400" : "w-1.5 bg-slate-700"
              )}
            />
          ))}
        </div>

        <div className="flex items-center gap-4">
          {!isLastStep && (
            <button
              {...skipProps}
              className="text-xs font-medium text-slate-500 transition-colors hover:text-slate-300"
            >
              Skip
            </button>
          )}
          <button
            {...primaryProps}
            className="rounded-full bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm shadow-black/30 transition-colors hover:bg-blue-500"
          >
            {isLastStep ? "Done" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SetupTour() {
  const { user } = useAuth();
  const isMobile = useMobile();
  const { setMobileOpen } = useSidebar();
  const [run, setRun] = useState(false);
  const isAdmin = user?.role === "admin";
  const tourDoneKey = user ? `${TOUR_DONE_KEY_PREFIX}${user.id}` : null;

  const { data: applications = [], isLoading: appsLoading } = useQuery<any[]>({
    queryKey: ["/api/applications"],
    enabled: !!user,
  });
  const { data: licenses = [], isLoading: licensesLoading } = useQuery<any[]>({
    queryKey: ["/api/licenses"],
    enabled: !!user,
  });
  // People/Team steps (and their completion checks) only apply to admins, since
  // regular members don't have those sidebar items at all.
  const { data: employees = [], isLoading: employeesLoading } = useQuery<any[]>({
    queryKey: ["/api/employees"],
    enabled: !!user && isAdmin,
  });
  const { data: teamUsers = [], isLoading: teamLoading } = useQuery<any[]>({
    queryKey: ["/api/users/team"],
    enabled: !!user && isAdmin,
    retry: false,
  });

  const isLoading = appsLoading || licensesLoading || (isAdmin && (employeesLoading || teamLoading));

  const hasApplications = Array.isArray(applications) && applications.length > 0;
  const hasLicenses = Array.isArray(licenses) && licenses.length > 0;
  const hasEmployees = Array.isArray(employees) && employees.length > 0;
  const hasInvitedTeam = Array.isArray(teamUsers) && teamUsers.length > 1;

  const steps: Step[] = isAdmin
    ? [APPLICATIONS_STEP, LICENSES_STEP, PEOPLE_STEP, TEAM_STEP]
    : [APPLICATIONS_STEP, LICENSES_STEP];

  const allDone = isAdmin
    ? hasApplications && hasLicenses && hasEmployees && hasInvitedTeam
    : hasApplications && hasLicenses;

  useEffect(() => {
    if (!user || !tourDoneKey || isLoading) return;
    if (localStorage.getItem(tourDoneKey) === "true") return;

    if (allDone) {
      localStorage.setItem(tourDoneKey, "true");
      return;
    }

    // Mark as seen the moment the tour is shown, not only once finished/skipped --
    // otherwise navigating away or reloading mid-tour (before Joyride's callback
    // fires) leaves nothing saved, and it shows again on every subsequent login.
    localStorage.setItem(tourDoneKey, "true");

    // All nav steps live inside the hamburger menu sheet on mobile, so open it
    // for the duration of the tour and give it a moment to mount/animate in
    // before Joyride measures the target's position.
    if (isMobile) {
      setMobileOpen(true);
      const timer = setTimeout(() => setRun(true), MOBILE_MENU_OPEN_DELAY_MS);
      return () => clearTimeout(timer);
    }

    setRun(true);
  }, [user, tourDoneKey, isMobile, isLoading, allDone, setMobileOpen]);

  useEffect(() => {
    if (!user) return;

    const handleReplay = () => {
      if (tourDoneKey) localStorage.removeItem(tourDoneKey);
      if (isMobile) {
        setMobileOpen(true);
        setTimeout(() => setRun(true), MOBILE_MENU_OPEN_DELAY_MS);
      } else {
        setRun(true);
      }
    };

    window.addEventListener(REPLAY_TOUR_EVENT, handleReplay);
    return () => window.removeEventListener(REPLAY_TOUR_EVENT, handleReplay);
  }, [user, tourDoneKey, isMobile, setMobileOpen]);

  const handleCallback = useCallback(
    (data: CallBackProps) => {
      const { status } = data;
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        if (tourDoneKey) localStorage.setItem(tourDoneKey, "true");
        setRun(false);
        if (isMobile) setMobileOpen(false);
      }
    },
    [tourDoneKey, isMobile, setMobileOpen],
  );

  if (!user) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      scrollToFirstStep
      callback={handleCallback}
      tooltipComponent={TourTooltip}
      styles={{
        options: {
          primaryColor: "#0d9488",
          // The tooltip draws its own curved dashed pointer, so hide Joyride's default
          // solid-triangle arrow by making it transparent.
          arrowColor: "transparent",
          overlayColor: "rgba(15, 23, 42, 0.65)",
          zIndex: 10000,
        },
        spotlight: {
          borderRadius: 12,
        },
      }}
    />
  );
}
